import { sinkValue, XorOfUnions } from "@redotech/util/type";

export enum TimeframeConditionType {
  /** For timeframes like "In the last 5 days" */
  BEFORE_NOW_RELATIVE = "before-now-relative",
  /** Example: 5 days ago */
  BEFORE_NOW_RELATIVE_EXACT = "before-now-relative-exact",
  /** Example: After 2024-12-25 */
  AFTER = "after",
  /** Example: 5 days from now */
  AFTER_NOW_RELATIVE_EXACT = "after-now-relative-exact",
  /** Example: Before 2024-12-25 */
  BEFORE = "before",
  /** Example: Between 5 and 10 days ago */
  BETWEEN_RELATIVE = "between-relative",
  /** Example: Between 2024-12-25 and 2024-12-30 */
  BETWEEN_DATES = "between-dates",
  /** Example: At least 5 days ago */
  BEFORE_RELATIVE = "before-relative",
  /** Accepts all dates */
  ALL_TIME = "all-time",
  /** Example: On 2024-12-25 */
  ON = "on",
  /** Accepts today's date */
  TODAY = "today",
  /** Any time since the automation started */
  AUTOMATION_START = "automation-start",
  /** Accepts any date in the current week */
  THIS_WEEK = "this-week",
  /** Accepts any date in the current month */
  THIS_MONTH = "this-month",
}

export const fullDateTimeframeConditionTypes = [
  TimeframeConditionType.BEFORE_NOW_RELATIVE,
  TimeframeConditionType.AFTER,
  TimeframeConditionType.BEFORE,
  TimeframeConditionType.BETWEEN_RELATIVE,
  TimeframeConditionType.BETWEEN_DATES,
  TimeframeConditionType.BEFORE_RELATIVE,
  TimeframeConditionType.ALL_TIME,
  TimeframeConditionType.ON,
  TimeframeConditionType.TODAY,
  TimeframeConditionType.AUTOMATION_START,
] as const;

export type FullDateTimeframeCondition =
  | BeforeTimeframeCondition
  | AfterTimeframeCondition
  | AllTimeTimeframeCondition
  | BetweenRelativeTimeframeCondition
  | BetweenDatesTimeframeCondition
  | BeforeRelativeTimeframeCondition
  | BeforeNowRelativeTimeframeCondition
  | OnTimeframeCondition
  | TodayTimeframeCondition
  | AutomationStartTimeframeCondition;

export interface BeforeNowRelativeTimeframeCondition {
  type: TimeframeConditionType.BEFORE_NOW_RELATIVE;
  value: number;
  units: TimeframeUnit;
}

export interface OnTimeframeCondition {
  type: TimeframeConditionType.ON;
  date: Date;
}

export interface BeforeRelativeTimeframeCondition {
  type: TimeframeConditionType.BEFORE_RELATIVE;
  value: number;
  units: TimeframeUnit;
}
export interface BeforeTimeframeCondition {
  type: TimeframeConditionType.BEFORE;
  date: Date;
}

export interface AfterTimeframeCondition {
  type: TimeframeConditionType.AFTER;
  date: Date;
}

export interface AllTimeTimeframeCondition {
  type: TimeframeConditionType.ALL_TIME;
}

export interface TodayTimeframeCondition {
  type: TimeframeConditionType.TODAY;
}

export interface AutomationStartTimeframeCondition {
  type: TimeframeConditionType.AUTOMATION_START;
}
export interface BetweenRelativeTimeframeCondition {
  type: TimeframeConditionType.BETWEEN_RELATIVE;
  start: number;
  end: number;
  units: TimeframeUnit;
}

export interface BetweenDatesTimeframeCondition {
  type: TimeframeConditionType.BETWEEN_DATES;
  range: [Date, Date];
}

export interface BeforeNowRelativeExactTimeframeCondition {
  type: TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT;
  value: number;
  units: TimeframeUnit;
}

export interface AfterNowRelativeExactTimeframeCondition {
  type: TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT;
  value: number;
  units: TimeframeUnit;
}
export interface ThisWeekTimeframeCondition {
  type: TimeframeConditionType.THIS_WEEK;
}

export interface ThisMonthTimeframeCondition {
  type: TimeframeConditionType.THIS_MONTH;
}

export type AnnualTimeframeCondition =
  | BeforeNowRelativeTimeframeCondition
  | AfterTimeframeCondition
  | BeforeTimeframeCondition
  | BetweenDatesTimeframeCondition
  | OnTimeframeCondition
  | BeforeNowRelativeExactTimeframeCondition
  | AfterNowRelativeExactTimeframeCondition
  | TodayTimeframeCondition
  | ThisWeekTimeframeCondition
  | ThisMonthTimeframeCondition;

export const annualTimeframeConditionTypes = [
  TimeframeConditionType.BEFORE_NOW_RELATIVE,
  TimeframeConditionType.AFTER,
  TimeframeConditionType.BEFORE,
  TimeframeConditionType.BETWEEN_DATES,
  TimeframeConditionType.ON,
  TimeframeConditionType.BEFORE_NOW_RELATIVE_EXACT,
  TimeframeConditionType.AFTER_NOW_RELATIVE_EXACT,
  TimeframeConditionType.TODAY,
  TimeframeConditionType.THIS_WEEK,
  TimeframeConditionType.THIS_MONTH,
] as const;

// Some types in TimeframeConditionType are used for both full date and annual timeframe conditions.
// What I really want to do is have one enum with the shared types and two subenums that extend it.
// Since typescript doesn't support that, TimeframeConditionType has all the condition types,
// and fullDateTimeframeConditionTypes and annualTimeframeConditionTypes are subsets of it.
// The anonymous functions below provide type safety for the subsets.

// eslint-disable-next-line no-unused-expressions
() => {
  // Ensure that fullDateTimeframeConditionTypes is a subset of TimeframeConditionType
  const a: readonly TimeframeConditionType[] = fullDateTimeframeConditionTypes;
  sinkValue(a);
  // Ensure that fullDateTimeframeConditionTypes and FullDateTimeframeCondition["type"] match
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const b = "" as unknown as XorOfUnions<
    FullDateTimeframeCondition["type"],
    (typeof fullDateTimeframeConditionTypes)[number]
  >;
  b satisfies never;
};

// eslint-disable-next-line no-unused-expressions
() => {
  // Ensure that annualTimeframeConditionTypes is a subset of TimeframeConditionType
  const a: readonly TimeframeConditionType[] = annualTimeframeConditionTypes;
  sinkValue(a);
  // Ensure that annualTimeframeConditionTypes and AnnualTimeframeCondition["type"] match
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const b = "" as unknown as XorOfUnions<
    AnnualTimeframeCondition["type"],
    (typeof annualTimeframeConditionTypes)[number]
  >;
  b satisfies never;
};

export enum TimeframeUnit {
  HOUR = "hour",
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
}

export const timeframeUnitToTemporalDurationLike: Record<
  TimeframeUnit,
  keyof Temporal.DurationLike
> = { hour: "hours", day: "days", week: "weeks", month: "months" };
